import React, { useCallback, useEffect, useRef } from "react";

import { ColumnApi, ColumnVisibleEvent, GridApi, GridReadyEvent } from "@ag-grid-community/core";
import cn from "classnames";

import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import ServerSideGrid, { ServerSideGridProps } from "@app/component/grid/ServerSideGrid";
import ColumnVisibility from "@app/types/viewModel/ColumnVisibility";
import useAnalytics from "@app/util/contexts/useAnalytics";
import defaultIdGetter from "@app/util/defaultIdGetter";

import mergeFilterModels from "../utils/mergeFilterModels";

import styles from "./AgGridListViewServerSideGrid.module.scss";
import ListViewComponent from "./types/ListViewComponent";
import { useAgGridListView } from "./utils/useListView";

interface Props<T> extends Omit<ServerSideGridProps<T>, "fetcher" | "fieldModel"> {
  showCreateCta?: boolean; // in NoRows
}

const AgGridListViewServerSideGrid = <T extends AnyEntity>({
  className,
  onGridReady: onGridReadyCallback,
  showCreateCta = true,
  ...props
}: Props<T>) => {
  const {
    columnDefs,
    columnToolPanelColumns,
    entityType,
    fieldModel,
    onColumnModelChange,
    onGridReady,
    register,
    totalRecords,
    unregister,
    updateViewState,
    updateViewStateAndFetch,
    viewState,
  } = useAgGridListView<T>();

  const gridApi = useRef<GridApi>();
  const columnApi = useRef<ColumnApi>();

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridApi.current = event.api;
      columnApi.current = event.columnApi;
      onGridReady(event.api);
      onGridReadyCallback?.(event);
    },
    [onGridReady, onGridReadyCallback]
  );

  const { getRowNodeId } = props;
  const analyticIssuer = useAnalytics();
  const handleUpdateRowData: ListViewComponent<T>["onUpdateEntity"] = useDynamicCallback(
    (newData: T) => {
      const id = getRowNodeId ? getRowNodeId(newData) : defaultIdGetter(newData);
      const rowNode = gridApi.current?.getRowNode(id);
      if (rowNode) {
        rowNode.setData(newData);
      }
    }
  );

  const handleExternalFilterChanged: ListViewComponent<T>["onFilterChange"] = useDynamicCallback(
    (newFilterModel: Partial<FilterModel>) => {
      // first of all, update the list view state
      const resultingFilterModel = mergeFilterModels(viewState.filter, newFilterModel);
      updateViewState({ filter: resultingFilterModel });
      //  let grid know that the filter has changed to trigger data reloading
      gridApi.current?.onFilterChanged();
    }
  );

  const handleExternalVisibilityChanged: ListViewComponent<T>["onColumnVisibilityChange"] =
    useDynamicCallback((columnVisibilities: ColumnVisibility[]) => {
      columnApi.current?.setColumnsVisible(
        columnVisibilities.filter(({ visible }) => visible).map(({ field }) => field.name),
        true
      );
      columnApi.current?.setColumnsVisible(
        columnVisibilities.filter(({ visible }) => !visible).map(({ field }) => field.name),
        false
      );
    });

  useEffect(() => {
    const callbacks: ListViewComponent<T> = {
      onColumnVisibilityChange: handleExternalVisibilityChanged,
      onFilterChange: handleExternalFilterChanged,
      onUpdateEntity: handleUpdateRowData,
    };
    register(callbacks);
    return () => unregister(callbacks);
  }, [
    handleExternalFilterChanged,
    handleExternalVisibilityChanged,
    handleUpdateRowData,
    register,
    unregister,
  ]);

  useChangeTracking(() => {
    analyticIssuer?.clicked(
      [Object.keys(viewState.filter).length ? "Filter Change" : "Filter Clear"],
      { filter: viewState.filter }
    );
  }, [analyticIssuer, viewState.filter]);

  const handleColumnVisibilityChange = useCallback(
    (event: ColumnVisibleEvent) => {
      if (event.columns && event.columns.length) {
        const column = event.columns[0];
        analyticIssuer?.clicked([
          "Change Column Visibility",
          column.getColDef().headerName ?? "",
          column.isVisible() ? "Show" : "Hide",
        ]);
      }
    },
    [analyticIssuer]
  );

  return (
    <ServerSideGrid
      className={cn(styles.container, className)}
      columnDefs={columnDefs}
      columnToolPanelColumns={columnToolPanelColumns}
      fetcher={updateViewStateAndFetch}
      fieldModel={fieldModel}
      initialSortModel={viewState.sort}
      noRowsOverlayComponentParams={{
        entityType,
        showCreateCta,
        totalRecords,
      }}
      onColumnModelChange={onColumnModelChange}
      onColumnVisible={handleColumnVisibilityChange}
      onGridReady={handleGridReady}
      {...props}
    />
  );
};

export default AgGridListViewServerSideGrid;
